import { Injectable } from "@angular/core";
import { v4 as uuid } from "uuid";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";

interface PerformanceMetricStep {
  label: string;
  elapsedMs: number;
  meta: Record<string, any>;
}

export interface PerformanceMetric {
  id: string;
  operation: string;
  durationMs: number;
  status: "success" | "error";
  startTimestamp: number;
  meta: Record<string, any>;
  steps: PerformanceMetricStep[];
}

interface PerformanceMetricMarker {
  id: string;
  operation: string;
  startTime: number;
  startTimestamp: number;
  meta: Record<string, any>;
  steps: PerformanceMetricStep[];
}

@Injectable({
  providedIn: "root",
})
export class PerformanceMetricsService extends BaseService {
  private markers = new Map<string, PerformanceMetricMarker>();

  constructor(logService: LogService) {
    super(logService, "PerformanceMetricsService");
  }

  private now(): number {
    if (typeof performance !== "undefined" && performance.now) {
      return performance.now();
    }
    return Date.now();
  }

  public start(operation: string, meta: Record<string, any> = {}): string {
    const id = uuid();
    const marker: PerformanceMetricMarker = {
      id,
      operation,
      startTime: this.now(),
      startTimestamp: Date.now(),
      meta,
      steps: [],
    };
    this.markers.set(id, marker);
    return id;
  }

  public step(id: string, label: string, meta: Record<string, any> = {}): void {
    const marker = this.markers.get(id);
    if (!marker) {
      this.logger.warn("performance metric step skipped", {
        id,
        label,
        meta,
      });
      return;
    }
    marker.steps.push({
      label,
      elapsedMs: this.now() - marker.startTime,
      meta,
    });
  }

  public stop(
    id: string,
    status: "success" | "error" = "success",
    meta: Record<string, any> = {},
  ): PerformanceMetric | undefined {
    const marker = this.markers.get(id);
    if (!marker) {
      this.logger.warn("performance metric stop skipped", { id, status, meta });
      return undefined;
    }
    this.markers.delete(id);
    const metric: PerformanceMetric = {
      id: marker.id,
      operation: marker.operation,
      durationMs: this.now() - marker.startTime,
      status,
      startTimestamp: marker.startTimestamp,
      meta: { ...marker.meta, ...meta },
      steps: marker.steps,
    };
    this.logger.info("performance metric", metric);
    return metric;
  }

  public cancel(id: string): void {
    if (this.markers.has(id)) {
      this.logger.warn("performance metric cancelled", { id });
      this.markers.delete(id);
    }
  }
}
